import React, { useCallback, useMemo, useState } from "react";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import FeedVideoPlayer from "views_shared/app/javascript/components/FeedVideoPlayer";
import SearchableTable from "../shared/SearchableTable";
import { createPostgRESTDataAdapter } from "../shared/dataAdapters";
import FeedPostApprovalPanel from "./FeedPostApprovalPanel";

const SUPABASE_URL = () => globalThis.Qwoted.SUPABASE_API_EXTERNAL_URL;

const TABS = [
  { key: "manual", label: "Needs Review" },
  { key: "approved", label: "Approved" },
  { key: "ghosted", label: "Ghosted" },
  { key: "all", label: "All" },
];

const VALID_TAB_KEYS = new Set(TABS.map((tab) => tab.key));

// Columns are the same set across tabs (the queue is small and uniform); each tab
// just changes the static filter + default sort. Every key here must be a real
// column on admin_feed_post_review_queue — the PostgREST adapter selects all of
// them. The composite "Post" cell is keyed on the real profile_name column (and
// enriched from the other hidden columns in renderCell), and the inline actions
// column is provided out-of-band via SearchableTable's renderActions prop, so
// neither is a fabricated select column.
const COLUMNS = {
  id: { type: "number", hidden: true },
  profile_name: { type: "string", display_as: "Post" },
  kind: { type: "string", hidden: true },
  body: { type: "string", hidden: true },
  work_url: { type: "string", hidden: true },
  work_title: { type: "string", hidden: true },
  work_outlet: { type: "string", hidden: true },
  profile_image_url: { type: "string", hidden: true },
  automation_reason: { type: "string", display_as: "AI decision" },
  automation_decision: { type: "string", hidden: true },
  visibility: { type: "string", display_as: "Status" },
  created_at: { type: "datetime", display_as: "Posted" },
  approved_at: { type: "datetime", hidden: true },
  ghosted_at: { type: "datetime", hidden: true },
  needs_manual_approval_at: { type: "datetime", hidden: true },
  assigned_to_id: { type: "string", hidden: true },
  assigned_to_name: { type: "string", hidden: true },
  video_attachment_id: { type: "number", hidden: true },
};

const TAB_CONFIGS = {
  manual: {
    staticFilters: { needs_manual_approval_at: "not.is.null", approved_at: "is.null", ghosted_at: "is.null" },
    defaultSort: { needs_manual_approval_at: "asc" },
  },
  approved: {
    staticFilters: { approved_at: "not.is.null", ghosted_at: "is.null" },
    defaultSort: { approved_at: "desc" },
  },
  ghosted: {
    staticFilters: { ghosted_at: "not.is.null" },
    defaultSort: { ghosted_at: "desc" },
  },
  all: {
    staticFilters: {},
    defaultSort: { created_at: "desc" },
  },
};

function getTabFromUrl() {
  const tab = new URLSearchParams(window.location.search).get("tab");
  return tab && VALID_TAB_KEYS.has(tab) ? tab : "manual";
}

function setTabInUrl(tab) {
  const url = new URL(window.location);
  url.searchParams.set("tab", tab);
  window.history.replaceState({}, "", url);
}

function statusBadge(row) {
  if (row.ghosted_at) return <span className="badge bg-danger">Ghosted</span>;
  if (row.approved_at) return <span className="badge bg-success">Approved</span>;
  if (row.needs_manual_approval_at) return <span className="badge bg-warning text-dark">Needs review</span>;
  return <span className="badge bg-info text-dark">Processing</span>;
}

// Plain-text preview of a post body (the stored body is sanitised HTML — strip
// tags for the queue snippet rather than rendering markup in the admin table).
function bodyPreview(body, max = 140) {
  const text = (body || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

function FeedPostsAdminPanel() {
  const [activeTab, setActiveTab] = useState(getTabFromUrl);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    setTabInUrl(tab);
  }, []);

  const refresh = useCallback(() => setRefreshTrigger((prev) => prev + 1), []);

  const tabConfig = TAB_CONFIGS[activeTab];

  const dataAdapter = useMemo(
    () =>
      createPostgRESTDataAdapter({
        endpoint: `${SUPABASE_URL()}/rest/v1`,
        resource: "admin_feed_post_review_queue",
        headers: authenticatedHeadersHash(),
        staticFilters: tabConfig.staticFilters,
        columns: COLUMNS,
        searchColumns: ["body", "work_title", "work_outlet", "profile_name"],
      }),
    [tabConfig.staticFilters],
  );

  const renderCell = (row, column) => {
    if (column === "profile_name") {
      return (
        <div className="d-flex align-items-start">
          {row.profile_image_url ? (
            <img
              src={row.profile_image_url}
              alt=""
              style={{ width: 32, height: 32, objectFit: "cover", borderRadius: "50%", flexShrink: 0 }}
              className="me-2"
            />
          ) : (
            <div
              className="me-2 bg-light d-flex align-items-center justify-content-center text-muted"
              style={{ width: 32, height: 32, borderRadius: "50%", fontSize: "0.75rem", flexShrink: 0 }}
            >
              <i className="fa fa-user" />
            </div>
          )}
          <div>
            <div className="fw-medium">{row.profile_name || "Unknown author"}</div>
            {row.kind === "share_work" ? (
              <div className="text-muted small">
                <span className="badge bg-light text-dark border me-1">Shared work</span>
                {row.work_title || row.work_url}
                {row.work_outlet && ` — ${row.work_outlet}`}
              </div>
            ) : (
              <div className="text-muted small">{bodyPreview(row.body)}</div>
            )}
            {row.video_attachment_id && (
              // The reviewer watches the video (+ reads the transcript) right in the queue before
              // deciding — the manual-review gate needs eyes on the actual content.
              <div className="mt-2" style={{ maxWidth: 320 }}>
                <FeedVideoPlayer attachmentId={row.video_attachment_id} />
              </div>
            )}
            {row.assigned_to_name && (
              <span className="badge bg-info text-dark mt-1" style={{ fontSize: "0.65em" }}>
                Assigned: {row.assigned_to_name}
              </span>
            )}
          </div>
        </div>
      );
    }

    if (column === "automation_reason") {
      if (!row.automation_reason && !row.automation_decision) return "—";
      return (
        <div className="small">
          {row.automation_decision && <div className="fw-medium">{row.automation_decision}</div>}
          {row.automation_reason && <div className="text-muted">{row.automation_reason}</div>}
        </div>
      );
    }

    if (column === "visibility") {
      return statusBadge(row);
    }

    return undefined;
  };

  const renderActions = (row) => <FeedPostApprovalPanel post={row} onActionComplete={refresh} />;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="mb-0">Feed Posts</h1>
      </div>

      <ul className="nav nav-tabs mb-0">
        {TABS.map((tab) => (
          <li className="nav-item" key={tab.key}>
            <button
              type="button"
              className={`nav-link ${activeTab === tab.key ? "active" : ""}`}
              onClick={() => handleTabChange(tab.key)}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      <SearchableTable
        columns={COLUMNS}
        searchColumns={["body", "work_title", "work_outlet", "profile_name"]}
        dataAdapter={dataAdapter}
        pageSize={25}
        defaultSort={tabConfig.defaultSort}
        renderCell={renderCell}
        renderActions={renderActions}
        enableFiltering={false}
        refreshTrigger={refreshTrigger}
      />
    </div>
  );
}

export default FeedPostsAdminPanel;
